import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { toast } from "sonner";

export const useCommunityGroups = () => {
    const { user } = useAuth();
    const { activeProperty } = usePropertyContext(); // Keep this fixed at the top
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchGroups = useCallback(async () => {
        if (!user || !activeProperty) return;
        setLoading(true);

        const { data, error } = await supabase
            .from("community_group_members")
            .select(`
                group_id,
                community_groups (
                    id,
                    name,
                    invite_code,
                    created_at
                )
            `)
            .eq("user_id", user.id)
            .eq("property_id", activeProperty.id);

        if (error) {
            console.error("Error fetching groups:", error);
        } else {
            setGroups(data?.map(item => item.community_groups) || []);
        }
        setLoading(false);
    }, [user, activeProperty]);

    const createGroup = async (name) => {
        if (!user) return null;

        const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

        const { data: group, error: groupError } = await supabase
            .from("community_groups")
            .insert({
                name,
                invite_code: inviteCode,
                created_by: user.id
            })
            .select()
            .single();

        if (groupError) {
            console.error("Group creation error:", groupError);
            toast.error(`Failed to create group: ${groupError.message}`);
            return null;
        }

        // Add creator as member
        const { error: memberError } = await supabase
            .from("community_group_members")
            .insert({
                group_id: group.id,
                user_id: user.id,
                property_id: activeProperty?.id
            });

        if (memberError) {
            console.error("Member join error:", memberError);
            toast.error("Group created, but failed to join yourself to it");
            return null;
        }

        toast.success(`Group "${name}" created!`);
        fetchGroups();
        return group;
    };

    const joinGroup = async (inviteCode) => {
        if (!user) return null;

        // Find group by invite code
        const { data: group, error: groupError } = await supabase
            .from("community_groups")
            .select("*")
            .eq("invite_code", inviteCode.toUpperCase())
            .single();

        if (groupError || !group) {
            toast.error("Invalid invite code");
            return null;
        }

        const { error: memberError } = await supabase
            .from("community_group_members")
            .insert({
                group_id: group.id,
                user_id: user.id,
                property_id: activeProperty?.id
            });

        if (memberError) {
            if (memberError.code === "23505") {
                toast.error("You are already a member of this group");
            } else {
                toast.error("Failed to join group");
            }
            return null;
        }

        toast.success(`Joined ${group.name}!`);
        fetchGroups();
        return group;
    };

    const leaveGroup = async (groupId) => {
        if (!user) return false;

        const { error } = await supabase
            .from("community_group_members")
            .delete()
            .eq("group_id", groupId)
            .eq("user_id", user.id)
            .eq("property_id", activeProperty?.id);

        if (error) {
            toast.error("Failed to leave group");
            return false;
        }

        toast.success("Left group");
        fetchGroups();
        return true;
    };

    useEffect(() => {
        let active = true;
        const timer = setTimeout(() => {
            if (active) {
                fetchGroups();
            }
        }, 0);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [fetchGroups]);

    return {
        groups,
        loading,
        createGroup,
        joinGroup,
        leaveGroup,
        refetch: fetchGroups
    };
};
